// Mount-only treatment — "Mounted (on substrate)" — Michael 2026-09-30. Staff ($xxlAdmin) or a customer account flagged mountOnlyEnabled ($mountOnlyCustomer, 2026-10-01).
//
// A Fine Art Print adhered to a rigid sheet, NO frame (optionally set in a floater frame), so staff
// can build mount carts in the visualizer and the work order / cut list read them. Mirrors the quote
// tool's "Mounting" treatment (tpw-tools quote_form.html): Substrate (Foam 3/16" / 1/8" Sintra /
// Dibond), optional floater frame, wood brace (for hanging — not on foam), French cleat (no charge).
// Priced by the backend (utils/pricing/mountOnlyPricing.ts): print (paper tier) + the mount SHEET
// that fits the finished print (image + full border) + brace + floater (canvas floater tier). NOT the
// framed mount add-on (frameMount.ts — substrate replaces the backing, by glass, own scaling).
//
// Stored on the visualizer options as `mountOnly = { substrate, brace, cleat, frame }`; the cart line
// carries the Create-Item fields (itemJson.mountSubstrate / brace / cleat, frameOptions.label) so the
// Master List (`mntd on dbond in BLK FF w/ brace`), work order and cut list read a visualizer line and
// a Create-Item line the same way (framingSpecVocab.ts MOUNT_SUBSTRATE_OPTIONS = the codes).
import { atom } from "nanostores";
import { FRAME_GROUPS, MOUNT_SUBSTRATE_OPTIONS } from "@/utils/content/framingSpecVocab";

// Mount-only for a CUSTOMER account (2026-10-01): WebUser.mountOnlyEnabled, set by the Visualizer page from
// UserApi.getUser() alongside $xxlAdmin. Unlocks ONLY the Mounted tile + its options on the Giclée panel —
// none of the other staff-only parts (XXL sizes, custom moulding / mat / backing, Loose Canvas).
export const $mountOnlyCustomer = atom(false);

export const MOUNT_ONLY_TREATMENT = "Mounted (on substrate)";

export type MountOnlySpec = {
  /** Master List code: "3/16 FB" | "1/8 FB" | "sntra" | "dbond" (MOUNT_SUBSTRATE_OPTIONS). */
  substrate: string;
  brace: boolean;
  cleat: boolean;
  /** Floater frame label ("Black Floater Frame" …), "" = none. */
  frame: string;
  /** Floater reveal PER SIDE in inches (gap between print and floater) — one of MOUNT_ONLY_REVEALS; floater only. */
  reveal?: number;
};

export const DEFAULT_MOUNT_ONLY: MountOnlySpec = { substrate: "sntra", brace: false, cleat: false, frame: "", reveal: 0.25 };

// Floater reveal (Michael 10-13): 1/8, 1/4, 3/8 or 1/2" per side, never more. Default 1/4" = what every mount-only
// floater line carried before the choice existed. The floater face is 1/2" (the overall adds reveal + face per side).
export const MOUNT_ONLY_REVEALS: { value: string; label: string }[] = [
  { value: "0.125", label: '1/8"' },
  { value: "0.25", label: '1/4"' },
  { value: "0.375", label: '3/8"' },
  { value: "0.5", label: '1/2"' },
];
export const MOUNT_FLOATER_FACE = 0.5;
export const DEFAULT_MOUNT_REVEAL = 0.25;
/** A stored reveal, snapped to the four allowed values (anything else → the 1/4" default). */
export function mountOnlyReveal(raw: unknown): number {
  const n = Number(raw);
  return MOUNT_ONLY_REVEALS.some(r => Number(r.value) === n) ? n : DEFAULT_MOUNT_REVEAL;
}
export const mountRevealLabel = (r: number) =>
  MOUNT_ONLY_REVEALS.find(o => Number(o.value) === r)?.label ?? `${r}"`;

export type MountOnlySubstrateOption = { code: string; label: string; short: string; description: string };

/** The visualizer's three tiles — the quote tool's MOUNT_SUBSTRATES. */
export const MOUNT_ONLY_SUBSTRATES: MountOnlySubstrateOption[] = [
  { code: "3/16 FB", label: 'Foam (3/16")', short: "Foam", description: 'Print mounted to 3/16" foam board — light, up to 48x60. No wood brace.' },
  { code: "sntra", label: '1/8" Sintra', short: '1/8" Sintra', description: 'Print mounted to 1/8" Sintra (PVC) — rigid, light' },
  { code: "dbond", label: "Dibond", short: "Dibond", description: "Print mounted to Dibond (aluminum composite) — rigid, keeps large pieces flat" },
];

/** Floater frames = the vocabulary's floater group (the same list the quote tool offers). */
export const MOUNT_ONLY_FLOATERS: { value: string; label: string }[] =
  FRAME_GROUPS.find(g => /floater/i.test(g.group))?.items.map(o => ({ value: o.value, label: o.label })) ?? [];

// Sheets (either orientation), smallest first — the same list as the backend / quote tool.
export const MOUNT_ONLY_SHEETS: [number, number][] = [
  [8, 10], [11, 14], [16, 20], [20, 24], [20, 30], [24, 30], [30, 40], [40, 50], [40, 60], [48, 60], [48, 96], [60, 96],
];
const FOAM_MAX_SHEET: [number, number] = [48, 60];

export const mountOnlySheet = (w: number, h: number): [number, number] | null => {
  const lo = Math.min(w, h);
  const hi = Math.max(w, h);
  return MOUNT_ONLY_SHEETS.find(([a, b]) => lo <= a && hi <= b) ?? null;
};

export const isFoamCode = (code: string) => /fb|foam/i.test(code);

/** Substrate offered for this FINISHED print size (image + full border): any sheet; foam up to 48x60. */
export function mountOnlyFits(code: string, printW: number, printH: number): boolean {
  const sheet = mountOnlySheet(printW, printH);
  if (!sheet) return false;
  if (isFoamCode(code)) return Math.min(printW, printH) <= FOAM_MAX_SHEET[0] && Math.max(printW, printH) <= FOAM_MAX_SHEET[1];
  return true;
}

/** Floater / wood brace only on Sintra / Dibond (foam can't carry the structure); a floater always has the brace (Michael 09-30). */
export const mountOnlyFloaterAllowed = (code: string) => !isFoamCode(code);
export const mountOnlyEffectiveBrace = (spec: { substrate: string; brace: boolean; frame: string }) =>
  !isFoamCode(spec.substrate) && (!!spec.brace || !!String(spec.frame || "").trim());

export const isMountOnlyTreatment = (type: unknown) =>
  /mount/i.test(String(type ?? "")) && !/float|gallery/i.test(String(type ?? ""));

/** The options' mount-only spec, or null when not on the Mounted treatment. */
export function mountOnlyOf(options: any): MountOnlySpec | null {
  if (options?.display || !isMountOnlyTreatment(options?.treatmentOptions?.type)) return null;
  const raw = options?.mountOnly;
  const substrate = String(raw?.substrate || DEFAULT_MOUNT_ONLY.substrate);
  const frame = isFoamCode(substrate) ? "" : String(raw?.frame || "").trim(); // no floater on foam
  const brace = mountOnlyEffectiveBrace({ substrate, brace: !!raw?.brace, frame }); // floater ⇒ brace; never on foam
  return {
    substrate,
    brace,
    cleat: brace && !!raw?.cleat, // the cleat mounts to the brace — never without it (Michael 09-30)
    frame,
    reveal: mountOnlyReveal(raw?.reveal),
  };
}

/** Customer-facing substrate name (quote tool wording): 'Foam (3/16")' | '1/8" Sintra' | 'Dibond'. */
export function mountSubstrateName(code: string): string {
  const hit = MOUNT_ONLY_SUBSTRATES.find(s => s.code === code) || MOUNT_SUBSTRATE_OPTIONS.find(o => o.value === code);
  if (hit) return hit.label;
  return /dbond|dibond/i.test(code) ? "Dibond" : /sntra|sintra/i.test(code) ? '1/8" Sintra' : 'Foam (3/16")';
}

/** Work-order materials name — size-first lines never start the material with a fraction ('30x40 1/8" Sintra' reads as 40 1/8). */
export function mountSubstrateMaterial(code: string): string {
  return /dbond|dibond/i.test(code)
    ? "Dibond"
    : /sntra|sintra/i.test(code)
      ? 'Sintra (1/8")'
      : /1\/8/.test(code)
        ? 'Foam Board (1/8")'
        : 'Foam Board (3/16")';
}

/** "Black Floater Frame" as the spec line: the vocabulary label already ends in Frame / Floater. */
export function mountFloaterLabel(frame: string): string {
  const f = String(frame || "").trim();
  if (!f) return "";
  return /frame|floater|moulding/i.test(f) ? f : `${f} Frame`;
}
